import { resolveNoContent, resolveWithBody } from "./Utils/utils";
import { usePromisedRequest as promiseRequest } from "../Common/request";
import { Status } from "../Common/Competitors/types";
import { PaymentStatus } from "../Common/consts";

export type SortField = "lastName" | "created";
export type SortDirection = "asc" | "desc";
export type PaymentConfirmationFilter = "all" | "withConfirmation" | "withoutConfirmation";

export type CompetitorPaymentsSearchRequest = {
    query?: string | null;
    onlyMissingPayments?: boolean;
    onlyCompletePayments?: boolean;
    disqualified?: boolean;
    deleted?: boolean;
    userId?: number | null;
    parameterId?: number | null;
    eventRegistrationIds?: number[];
    paymentConfirmationFilter?: PaymentConfirmationFilter;
};

export type CompetitorPaymentsPageRequest = CompetitorPaymentsSearchRequest & {
    offset?: number;
    limit?: number;
    sortField?: SortField;
    sortDirection?: SortDirection;
};

export type BulkCompetitorPaymentUpdate = {
    searchCriteria: CompetitorPaymentsSearchRequest;
    complete: boolean;
    timestamp: string;
};

export type Payment = {
    competitor_id: number;
    amount: number;
    is_complete: boolean;
    currency: string;
    status: PaymentStatus;
    discountPercentage?: number;
};

export type PaymentDocument = {
    id: number;
    user_id: number;
    name: string;
    created: string;
};

export type CompetitorPaymentWithMetadata = {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    birthYear: number | null;
    birthDate: string | null;
    nationality: string;
    academy: string;
    academyId: number;
    branch: string;
    category: string;
    parameterId: number;
    userId: number;
    creationDate: string;
    payment: Payment;
    paymentDocument: PaymentDocument | null;
    status: Status;
    containsUnassignedDocuments: boolean;
    stageNumber: number | null;
};

export type CompetitorPaymentsWithMetadataPage = {
    result: CompetitorPaymentWithMetadata[];
    hasPrevPage: boolean;
    hasNextPage: boolean;
    selectedCategoryName: string | null;
};

export type CompetitorPaymentsCountResponse = {
    count: number;
    timestamp: string;
};

export default {
    countCompetitorPayments(eventId: number, searchRequest: CompetitorPaymentsSearchRequest): Promise<CompetitorPaymentsCountResponse> {
        const queryParams = new URLSearchParams();
        if (searchRequest.query) {
            queryParams.append("query", searchRequest.query);
        }
        if (searchRequest.onlyMissingPayments) {
            queryParams.append("onlyMissingPayments", searchRequest.onlyMissingPayments ? "1" : "0");
        }
        if (searchRequest.onlyCompletePayments) {
            queryParams.append("onlyCompletePayments", searchRequest.onlyCompletePayments ? "1" : "0");
        }
        if (searchRequest.disqualified) {
            queryParams.append("disqualified", searchRequest.disqualified ? "1" : "0");
        }
        if (searchRequest.deleted) {
            queryParams.append("deleted", searchRequest.deleted ? "1" : "0");
        }
        if (searchRequest.userId) {
            queryParams.append("userId", searchRequest.userId.toString());
        }
        if (searchRequest.parameterId) {
            queryParams.append("parameterId", searchRequest.parameterId.toString());
        }
        if (searchRequest.paymentConfirmationFilter && searchRequest.paymentConfirmationFilter !== "all") {
            queryParams.append("paymentConfirmationFilter", searchRequest.paymentConfirmationFilter);
        }

        return promiseRequest<CompetitorPaymentsCountResponse>({
            url: `/api/admin/events/${eventId}/payments/count?${queryParams.toString()}`,
            method: "GET",
            json: true,
        }).then(resolveWithBody());
    },
    getCompetitorPaymentsPage(eventId: number, inputParams: CompetitorPaymentsPageRequest): Promise<CompetitorPaymentsWithMetadataPage> {
        const queryParams = {};
        if (inputParams.offset) {
            queryParams["offset"] = inputParams.offset;
        }
        if (inputParams.limit) {
            queryParams["limit"] = inputParams.limit;
        }
        if (inputParams.onlyMissingPayments) {
            queryParams["onlyMissingPayments"] = inputParams.onlyMissingPayments ? 1 : 0;
        }
        if (inputParams.onlyCompletePayments) {
            queryParams["onlyCompletePayments"] = inputParams.onlyCompletePayments ? 1 : 0;
        }
        if (inputParams.disqualified) {
            queryParams["disqualified"] = inputParams.disqualified ? 1 : 0;
        }
        if (inputParams.deleted) {
            queryParams["deleted"] = inputParams.deleted ? 1 : 0;
        }
        if (inputParams.sortField) {
            queryParams["sortField"] = inputParams.sortField;
        }
        if (inputParams.sortDirection) {
            queryParams["sortDirection"] = inputParams.sortDirection;
        }
        if (inputParams.query) {
            queryParams["query"] = inputParams.query;
        }
        if (inputParams.userId) {
            queryParams["userId"] = inputParams.userId;
        }
        if (inputParams.parameterId) {
            queryParams["parameterId"] = inputParams.parameterId;
        }
        if (inputParams.paymentConfirmationFilter && inputParams.paymentConfirmationFilter !== "all") {
            queryParams["paymentConfirmationFilter"] = inputParams.paymentConfirmationFilter;
        }
        const queryParamString = new URLSearchParams(queryParams as Record<string, string>).toString();

        return promiseRequest<CompetitorPaymentsWithMetadataPage>({
            url: `/api/admin/events/${eventId}/payments?${queryParamString}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getSingleCompetitorPayment(eventId: number, competitorId: number, userId: number): Promise<CompetitorPaymentWithMetadata> {
        return promiseRequest<CompetitorPaymentWithMetadata>({
            url: `/api/admin/events/${eventId}/payments/${competitorId}?userId=${userId}`,
            method: "get",
            json: true,
        }).then(res => {
            if (res.statusCode !== 200) {
                throw Error(`Could not fetch payments from event ${eventId}.`);
            }

            return res.body;
        });
    },
    updateCompetitorPayment(eventId, eventCompetitorId, amount, isComplete) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/payments/${eventCompetitorId}`,
            method: "PUT",
            json: {
                eventCompetitorId: parseInt(eventCompetitorId, 10),
                amount: parseInt(amount, 10),
                complete: isComplete,
            },
        }).then(resolveNoContent());
    },
    detachOnlinePayment(eventId, eventRegistrationId) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/payments/actions/detach-online-payment`,
            method: "post",
            json: {
                eventRegistrationId,
            },
        }).then(resolveNoContent());
    },
    bulkUpdateCompetitorPayments(eventId: number, bulkUpdateRequest: BulkCompetitorPaymentUpdate) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/payments`,
            method: "PUT",
            json: bulkUpdateRequest,
        }).then(resolveNoContent());
    },
    getUnassignedPaymentDocumentsListing(eventId, userId): Promise<PaymentDocument[]> {
        return promiseRequest<PaymentDocument[]>({
            url: `/api/admin/events/${eventId}/users/${userId}/unassigned-payment-documents`,
            method: "GET",
            json: true,
        }).then(resolveWithBody());
    },
};
